import { useEffect, useMemo, useState } from "react";
import { RequiredLabel } from "../../component/requiredLabel/RequiredLabel";
import Dropdown from "../../component/dropdown/Dropdown";
import "./Scheduling.css";
import CustomDatePicker from "../../component/datePicker/DatePicker";
import Text from "../../component/text/Text";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import RadioButton from "../../component/radioButton/RadioButton";
import { APIGetTrips } from "../../config/utilities/APIs/tripsApi";
import { APIGetDrivers } from "../../config/utilities/APIs/driversApi";
import { APIGetVehicles } from "../../config/utilities/APIs/vehiclesApi";
import { useAuth } from "../../config/utilities/useAuth";
import LoadingSpinner from "../../component/loading/LoadingSpinner";
import { APIGetSchedules } from "../../config/utilities/APIs/schedulingApi";
import Table from "../../component/table/Table";
import { ToggleSwitch } from "../../component/toggle/ToggleSwitch";

export default function SchedulingForm({
  mode,
  existingData,
  onSubmit,
  handleReset,
  loading,
  setLoading,
  input,
  setInput,
  error,
  setError,
  setAction,
}) {
  const { privilege, tripTypes } = useAuth();
  const institutionId = privilege?.institutionId;
  const allowedTripTypeIds = useMemo(
    () => new Set((tripTypes || []).map((type) => type?.id).filter(Boolean)),
    [tripTypes]
  );
  const isMobileViewport =
    typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [initialInput, setInitialInput] = useState(null);
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [students, setStudents] = useState([]);
  const [originalStudents, setOriginalStudents] = useState([]);
  const [searchTerm, setSearchTerm ] = useState("");
  const [isAssignmentExpanded, setIsAssignmentExpanded] = useState(false);
  const isWeekendDate = (dateValue) => {
    if (!dateValue) return false;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return false;
    const day = date.getDay();
    return day === 0 || day === 6;
  };
  const formatSummaryDate = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  const buildSelectedDatesSummary = () => {
    if (input.dateType === "single" && input.from) {
      return `Selected: ${formatSummaryDate(input.from)} (1 day)`;
    }

    if (input.dateType === "custom" && input.from && input.to) {
      const fromDate = new Date(input.from);
      const toDate = new Date(input.to);
      if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
        return "";
      }

      const start = new Date(fromDate);
      const end = new Date(toDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      let dayCount = 0;
      const cursor = new Date(start);
      while (cursor <= end) {
        const day = cursor.getDay();
        const weekend = day === 0 || day === 6;
        if (input.includeWeekends || !weekend) {
          dayCount += 1;
        }
        cursor.setDate(cursor.getDate() + 1);
      }

      return `Selected: ${formatSummaryDate(input.from)} - ${formatSummaryDate(
        input.to
      )} (${dayCount} day${dayCount === 1 ? "" : "s"})`;
    }

    return "";
  };
  const selectedDatesSummary = buildSelectedDatesSummary();

  const headers = [
    { key: "name", name: "Name" },
    { key: "class", name: "Class" },
    { key: "stopName", name: "Stop" },
    mode === "view"
      ? { key: "status", name: "Status" }
      : { key: "exempted", name: "Exemption" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInput = { ...input, [name]: value };
    setInput(updatedInput);
  };

  const isScheduleFormValid = () => {
    let isValid = true;
    const nativeToday = new Date();
    nativeToday.setHours(0, 0, 0, 0);

    const fromDate = input.from ? new Date(input.from) : null;
    if (fromDate) fromDate.setHours(0, 0, 0, 0);
    const toDate = input.to ? new Date(input.to) : null;
    if (toDate) toDate.setHours(0, 0, 0, 0);

    const newErrors = {
      trip: input.trip === "",
      driver: input.driver === "",
      vehicle: input.vehicle === "",
      dateType: mode === "create" ? input.dateType === "" : false,
      from:
        mode === "create"
          ? !input.from ||
            (fromDate && fromDate < nativeToday) ||
            (!input.includeWeekends && isWeekendDate(input.from))
          : false,
      to:
        mode === "create" && input.dateType === "custom"
          ? !input.to ||
            (fromDate && toDate && toDate < fromDate) ||
            (!input.includeWeekends && isWeekendDate(input.to))
          : false,
    };

    setError(newErrors);

    isValid = Object.values(newErrors).every((val) => val === false);

    let isEdited = false;

    if (mode === "create") {
      isEdited = initialInput
        ? JSON.stringify(initialInput) !== JSON.stringify(input)
        : input.trip !== "" ||
          input.driver !== "" ||
          input.assistant !== "" ||
          input.vehicle !== "" ||
          input.dateType !== "" ||
          input.from ||
          input.to ||
          input.includeWeekends !== true;
    } else {
      isEdited = initialInput
        ? JSON.stringify(initialInput) !== JSON.stringify(input)
        : input.trip !== "" ||
          input.driver !== "" ||
          input.assistant !== "" ||
          input.vehicle !== "";
    }
    return isValid && isEdited;
  };

  useEffect(() => {
    const nativeToday = new Date();
    nativeToday.setHours(0, 0, 0, 0);

    const fromDate = input.from ? new Date(input.from) : null;
    const toDate = input.to ? new Date(input.to) : null;

    if (fromDate) fromDate.setHours(0, 0, 0, 0);
    if (toDate) toDate.setHours(0, 0, 0, 0);

    setError({
      trip: input.trip === "",
      driver: input.driver === "",
      vehicle: input.vehicle === "",
      dateType: mode === "create" ? input.dateType === "" : false,
      from:
        mode === "create"
          ? !input.from ||
            (fromDate && fromDate < nativeToday) ||
            (!input.includeWeekends && isWeekendDate(input.from))
          : false,
      to:
        mode === "create" && input.dateType === "custom"
          ? !input.to ||
            (fromDate && toDate && toDate < fromDate) ||
            (!input.includeWeekends && isWeekendDate(input.to))
          : false,
    });
  }, [input, mode, setError]);

  useEffect(() => {
    if (mode === "edit" || mode === "view") {
      setIsAssignmentExpanded(false);
    }
  }, [mode, existingData?._id]);

  useEffect(() => {
    if (mode === "edit" || mode === "view") {
      setIsAssignmentExpanded(false);
    }
  }, [mode, existingData?._id]);

  useEffect(() => {
    setIsSubmitDisabled(!isScheduleFormValid());
  }, [input, initialInput]);

  const fetchSingleSchedule = async () => {
    setLoading(true);
    try {
      const response = await APIGetSchedules(
        {
          scheduleId: existingData._id,
        }
      );

      if (response.status === 200) {
        const { data } = response.data;

        const studentData = data.attendances?.students;
        const filteredStudents = studentData
          ?.filter((student) => student.childId)
          .sort((a, b) => {
            if (a.status === "Exempted" && b.status !== "Exempted") return -1;
            if (a.status !== "Exempted" && b.status === "Exempted") return 1;
            return 0;
          });

        setStudents(filteredStudents);
        setOriginalStudents(filteredStudents);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching schedule data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && existingData) {
      fetchSingleSchedule();

      const formattedInput = {
        trip: existingData.tripDetails?.tripId || "",
        driver: existingData.driverDetails?.driverId || "",
        assistant: existingData.assistantDetails?.assistantId || "",
        vehicle: existingData.vehicleDetails?.vehicleId || "",
        dateType: "",
        from: null,
        to: null,
        includeWeekends: true,
        attendance: [],
      };
      setInitialInput(formattedInput);
      setInput(formattedInput);
    } else {
      setInitialInput(input);
    }
  }, [mode, existingData]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const [tripResponse, driverResponse, vehicleResponse] =
          await Promise.all([
            APIGetTrips(undefined, "active", setLoading, institutionId),
            APIGetDrivers(undefined, "active", setLoading, institutionId),
            APIGetVehicles(undefined, "active", setLoading, institutionId),
          ]);

        if (tripResponse.status === 200) {
          const rawTrips = tripResponse.data.data.trips;
          const filteredTrips = rawTrips.filter((trip) =>
            allowedTripTypeIds.has(trip?.type)
          );
          const formattedTrips = filteredTrips.map((trip) => ({
            value: trip._id,
            label: trip.name,
          }));
          if (
            (mode === "edit" || mode === "view") &&
            existingData?.tripDetails
          ) {
            setTrips([
              {
                value: existingData?.tripDetails?.tripId,
                label: existingData?.tripDetails?.tripName,
              },
              ...formattedTrips,
            ]);
          } else {
            setTrips(formattedTrips);
          }
        }

        if (driverResponse.status === 200) {
          const rawDrivers = driverResponse.data.data.drivers;
          const formattedDrivers = rawDrivers
            .filter((driver) => driver.type === "driver")
            .map((driver) => ({
              value: driver._id,
              label: driver.name,
            }));

          const formattedAssistants = rawDrivers
            .filter((driver) => driver.type === "assistant")
            .map((assistant) => ({
              value: assistant._id,
              label: assistant.name,
            }));

          setDrivers(formattedDrivers);

          if (mode === "edit" || mode === "view") {
            setAssistants(formattedAssistants);
          } else {
            setAssistants([
              { value: "noDriver", label: "No Assistant" },
              ...formattedAssistants,
            ]);
          }
        }

        if (vehicleResponse.status === 200) {
          const rawVehicles = vehicleResponse.data.data.vehicles;
          const formattedVehicles = rawVehicles.map((vehicle) => ({
            value: vehicle._id,
            label: vehicle.vehicleNumber + " - " + vehicle.busNumber,
          }));
          setVehicles(formattedVehicles);
        }
      } catch (error) {
        console.error("Error fetching trips or drivers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [allowedTripTypeIds, existingData, institutionId, mode, setLoading]);

  useEffect(() => {
    if (mode !== "create") return;
    if (!input.trip) return;
    const isSelectedTripAllowed = trips.some((trip) => trip.value === input.trip);
    if (!isSelectedTripAllowed) {
      setInput((prev) => ({ ...prev, trip: "" }));
    }
  }, [input.trip, mode, setInput, trips]);

  const handlePopoverAction = (childId) => {
    setInput((prev) => {
      const exempted = Array.isArray(prev.attendance) ? prev.attendance : [];

      const originalStudent = originalStudents.find(
        (s) => s.childId === childId
      );
      const currentStudent = students.find((s) => s.childId === childId);

      if (!originalStudent || !currentStudent) return prev;

      const toggledStatus =
        currentStudent.status === "Exempted" ? "Scheduled" : "Exempted";

      const isChanged = toggledStatus !== originalStudent.status;

      const updatedAttendance = exempted.filter((e) => e.childId !== childId);
      if (isChanged) {
        updatedAttendance.push({ childId, status: toggledStatus });
      }

      return {
        ...prev,
        attendance: updatedAttendance,
      };
    });

    setStudents((prevStudents) =>
      prevStudents.map((student) =>
        student.childId === childId
          ? {
              ...student,
              status: student.status === "Exempted" ? "Scheduled" : "Exempted",
            }
          : student
      )
    );
  };

  const handleWeekendToggle = (checked) => {
    setInput((prev) => {
      const next = {
        ...prev,
        includeWeekends: checked,
      };

      if (!checked) {
        if (isWeekendDate(next.from)) {
          next.from = "";
        }
        if (isWeekendDate(next.to)) {
          next.to = "";
        }
      }

      return next;
    });
  };

  const handleSubmitClick = () => {
    if (mode === "create") {
      const isSelectedTripAllowed = trips.some((trip) => trip.value === input.trip);
      if (!isSelectedTripAllowed) {
        setError((prev) => ({ ...prev, trip: true }));
        return;
      }
    }
    onSubmit();
  };

  const renderAssignmentFields = () => (
    <>
      <div className="dropdown-type-name-form-container">
        <div className="input-label">
          <RequiredLabel>Selected Trip</RequiredLabel>
        </div>
        <div className="input-container">
          <Dropdown
            disabled={mode === "edit" || mode === "view"}
            options={trips}
            name="trip"
            placeholder="Select a trip..."
            onChangeFunction={handleInputChange}
            input={input}
            width={isMobileViewport ? "100%" : "46.9rem"}
          />
        </div>
      </div>
      <div className="dropdown-type-name-form-container">
        <div className="input-label">
          <RequiredLabel>Assigned Driver</RequiredLabel>
        </div>
        <div className="input-container">
          <Dropdown
            disabled={mode === "view"}
            options={drivers}
            name="driver"
            placeholder="Select a Driver..."
            onChangeFunction={handleInputChange}
            input={input}
            width={isMobileViewport ? "100%" : "46.9rem"}
          />
        </div>
      </div>
      <div className="dropdown-type-name-form-container">
        <div className="input-label">Assigned Bus Assistant</div>
        <div className="input-container">
          <Dropdown
            disabled={mode === "view"}
            options={assistants}
            name="assistant"
            placeholder="Select a Bus Assistant..."
            onChangeFunction={handleInputChange}
            input={input}
            width={isMobileViewport ? "100%" : "46.9rem"}
          />
        </div>
      </div>
      <div className="dropdown-type-name-form-container">
        <div className="input-label">
          <RequiredLabel>Assigned Vehicle</RequiredLabel>
        </div>
        <div className="input-container">
          <Dropdown
            disabled={mode === "view"}
            options={vehicles}
            name="vehicle"
            placeholder="Select a Vehicle..."
            onChangeFunction={handleInputChange}
            input={input}
            width={isMobileViewport ? "100%" : "46.9rem"}
          />
        </div>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="content-loading-container">
        <LoadingSpinner
          className="loading-spinner"
          size="large"
          color="#ffb502"
        />
      </div>
    );
  }

  return (
    <>
      {(mode === "edit" || mode === "view") && (
        <div className="schedule-form-collapsible-section">
          <button
            type="button"
            className="schedule-form-collapsible-header"
            onClick={() => setIsAssignmentExpanded((prev) => !prev)}
          >
            <span>Trip Assignment Details</span>
            <span>{isAssignmentExpanded ? "Hide" : "Show"}</span>
          </button>
          {isAssignmentExpanded && renderAssignmentFields()}
        </div>
      )}
      {mode === "create" && renderAssignmentFields()}
      {(mode === "edit" || mode === "view") && (
        <div className="dropdown-type-name-form-container attendance-form-container">
          <div className="input-label">
            Attendance
            <div className="input-description">
              This table displays each student along with a checkbox to exempt
              them from the scheduled trip. Use the checkbox to mark students
              who will be exempted.
            </div>
          </div>
          <div
            className="input-container"
            style={{ flex: 1, paddingBottom: "1rem" }}
          >
            <Table
              data={students || []}
              headers={headers}
              module="attendance"
              density="compact"
              loading={loading}
              height="42rem"
              useBackendPagination={false}
              handlePopoverAction={handlePopoverAction}
              showSearchBar={true}
              totalRecords={students?.length || 0}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchText="Search by student name..."
            />
          </div>
        </div>
      )}
      {mode === "create" && (
        <div className="name-form-container">
          <div className="input-label">
            <RequiredLabel>Scheduled Range</RequiredLabel>
            <div className="input-description">
              This defines the days on which the trip will occur. Choose Single
              Day to schedule the trip on a specific day, or Custom Date Range
              to schedule it across multiple consecutive days.
            </div>
          </div>
          <div className="input-container">
            <div className="schedule-range-controls">
              <div className="weekend-toggle-container">
                <Text
                  size="0.875rem"
                  color="#344054"
                  weight="500"
                  align="left"
                  text="Allow weekend selection"
                />
                <ToggleSwitch
                  checked={!!input.includeWeekends}
                  onChange={handleWeekendToggle}
                  aria-label="Toggle weekend selection"
                />
              </div>
            </div>
            <div className="input-schedule-radio-buttons">
              <RadioButton
                name="dateType"
                value="single"
                text="Single Day"
                onChange={(e) => handleInputChange(e)}
                checked={input.dateType === "single"}
                disabled={mode !== "create"}
              />
              <RadioButton
                name="dateType"
                value="custom"
                text="Custom Date Range"
                onChange={(e) => handleInputChange(e)}
                checked={input.dateType === "custom"}
                disabled={mode !== "create"}
              />
            </div>
            <div className="input-date-containers">
              <CustomDatePicker
                disable={mode === "view" || input.dateType === ""}
                label={"From Date"}
                placeholder="Select a date"
                name="from"
                error={mode === "view"}
                setError={setError}
                input={input}
                setInput={setInput}
                errorMessage=""
                width={isMobileViewport ? "100%" : "22rem"}
                disablePast={true}
                maxDate={input.to ? input.to : null}
                shouldDisableDate={(date) =>
                  !input.includeWeekends && isWeekendDate(date)
                }
              />
              {input.dateType === "custom" && (
                <>
                  <Text
                    size="1rem"
                    color="#344054"
                    weight="500"
                    align="center"
                    text="to"
                    height="0rem"
                    lineHeight="1.4rem"
                  />
                  <CustomDatePicker
                    disable={mode === "view" || input.dateType !== "custom"}
                    label={"To Date"}
                    placeholder="Select a date"
                    name="to"
                    error={mode === "view"}
                    setError={setError}
                    input={input}
                    setInput={setInput}
                    errorMessage=""
                    width={isMobileViewport ? "100%" : "22rem"}
                    disablePast={true}
                    minDate={input.from ? input.from : null}
                    shouldDisableDate={(date) =>
                      !input.includeWeekends && isWeekendDate(date)
                    }
                  />
                </>
              )}
            </div>
            {selectedDatesSummary && (
              <Text
                size="0.8125rem"
                color="#667085"
                weight="400"
                align="left"
                text={selectedDatesSummary}
              />
            )}
          </div>
        </div>
      )}
      <div className="button-container-edit">
        <div className="scheduling-form-cancel-button-wrap">
          <SecondaryButton
            width="6rem"
            label="Cancel"
            imageIcon={false}
            onClickFunction={
              mode === "create" ? handleReset : () => setAction("daily")
            }
          />
        </div>
        {mode !== "view" && (
          <PrimaryButton
            width="6rem"
            disable={isSubmitDisabled}
            label={mode === "create" ? "Create" : "Save"}
            onClickFunction={handleSubmitClick}
            loading={loading}
          />
        )}
      </div>
    </>
  );
}
